// WheelsTab.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Stack } from "@mui/material";
import debounce from "lodash/debounce";

import List from "./List";
import ListHeader from "../ListComponents/ListHeader";
import CustomTabs from "../../../../components/Tabs";
import MagicWand from "../../../../assets/icons/MagicWand";
import CloneIcon from "../../../../assets/icons/CloneV2";
import HeartIcon from "../../../../assets/icons/Favourited";
import UnlcockedIcon from "../../../../assets/icons/UnlockedIcon";
import { colors } from "../../../../utils";
import {
  useInfiniteWheels,
  useAllTabCounts,
} from "../../../../services/queries";
import { useParams } from "react-router-dom";
import { getLoggedInUser } from "../../../../utils/helpers";
import { useQueryClient } from "react-query";
import Filter from "../../../../assets/icons/Filter";
import TextView from "../../../TextView";
import MultiSelect from "../ListComponents/MultiSelect";

const InitialFilterOptionsByCategory = {
  Created: [],
  Cloned: [],
  Unlocked: [],
  Favourited: [],
};

export const WheelCategories = {
  Created: "Created",
  Cloned: "Cloned",
  Unlocked: "Unlocked",
  Favourited: "Favourited",
};

const WheelsTab = ({ selectedTab, tabChild }) => {
  const [isGridSelect, setIsGridSelect] = useState(true);
  const [searchedText, setSearchedText] = useState("");

  const [selectedWheelCategory, setSelectedWheelCategory] = useState(
    WheelCategories.Created
  );
  const [filtersByCategory, setFiltersByCategory] = useState(
    InitialFilterOptionsByCategory
  );

  const scrollPositionsRef = useRef({
    Created: 0,
    Cloned: 0,
    Unlocked: 0,
    Favourited: 0,
  });

  const handleFilterChange = (selectedValues) => {
    setFiltersByCategory((prevFilters) => ({
      ...prevFilters,
      [selectedWheelCategory]: selectedValues, // Only update the selected category's filters
    }));
  };

  const [isFetching, setIsFetching] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    setFiltersByCategory(InitialFilterOptionsByCategory);
    setSelectedWheelCategory(WheelCategories.Created);
    setSearchedText("");
  }, [id]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
  } = useInfiniteWheels({
    selectedTab,
    selectedCategory: selectedWheelCategory,
    searchedText,
    userId: id,
    selectedFilters: filtersByCategory[selectedWheelCategory] || [], // Fetch relevant filters only
  });

  const tabCounts = useAllTabCounts(id, searchedText, filtersByCategory);
  const isTabCountLoading = false; // Since we're using real-time data, no loading state needed

  // Save the scroll position when switching tabs
  const handleTabChange = (event, newTab) => {
    const scrollableContainer = document.getElementById("MainScrollableView");
    if (scrollableContainer) {
      scrollPositionsRef.current[selectedWheelCategory] =
        scrollableContainer.scrollTop;
    }
    setSelectedWheelCategory(newTab);
  };

  // Restore the scroll position when switching back to a tab
  useEffect(() => {
    const scrollableContainer = document.getElementById("MainScrollableView");
    if (scrollableContainer) {
      scrollableContainer.scrollTop =
        scrollPositionsRef.current[selectedWheelCategory] || 0;
    }
  }, [selectedWheelCategory]);

  //remove the wheel from react query cache
  const queryClient = useQueryClient(); // Initialize queryClient

  const onWheelDelete = useCallback(
    (wheelId) => {
      try {
        queryClient.setQueryData(
          [
            "fetchWheels",
            selectedTab,
            selectedWheelCategory,
            searchedText,
            filtersByCategory[selectedWheelCategory] || [],
            id,
          ],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                results: page.results.filter((wheel) => wheel.id !== wheelId),
              })),
            };
          }
        );
      } catch (error) {
        console.error("Failed to update cache:", error);
      }
    },
    [
      queryClient,
      selectedTab,
      selectedWheelCategory,
      searchedText,
      filtersByCategory,
      id,
    ]
  );

  const onWheelReactivate = useCallback(
    (wheelId) => {
      try {
        queryClient.setQueryData(
          [
            "fetchWheels",
            selectedTab,
            selectedWheelCategory,
            searchedText,
            filtersByCategory[selectedWheelCategory] || [],
            id,
          ],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                results: page.results.map((wheel) =>
                  wheel.id === wheelId ? { ...wheel, is_active: true } : wheel
                ),
              })),
            };
          }
        );
      } catch (error) {
        console.error("Failed to update cache for reactivation:", error);
      }
    },
    [
      queryClient,
      selectedTab,
      selectedWheelCategory,
      searchedText,
      filtersByCategory,
      id,
    ]
  );

  const allWheels = data?.pages.flatMap((page) => page.results) || [];
  const isLoggedInUserProfile =
    parseInt(id) === parseInt(getLoggedInUser()?.id);
  const currentTabCount =
    data?.pages && data.pages.length > 0 ? data.pages[0]?.count : undefined;

  const loadMoreWheels = useCallback(
    debounce(() => {
      if (isFetching || !hasNextPage) return;
      setIsFetching(true);
      fetchNextPage().finally(() => setIsFetching(false));
    }, 200),
    [isFetching, fetchNextPage, hasNextPage]
  );

  useEffect(() => {
    const handleScroll = () => {
      const scrollableContainer = document.getElementById("MainScrollableView");
      if (
        scrollableContainer &&
        scrollableContainer.scrollTop + scrollableContainer.clientHeight >=
          scrollableContainer.scrollHeight - 900
      ) {
        loadMoreWheels();
      }
    };

    const scrollableContainer = document.getElementById("MainScrollableView");
    if (scrollableContainer) {
      scrollableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollableContainer) {
        scrollableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loadMoreWheels]);

  const filterOptionsByCategory = {
    Cloned: [
      { value: "is_live", label: "Live" },
      { value: "is_static", label: "Static" },
      // { value: 'is_edited_clone', label: 'Edited' },
      { value: "is_locked_clone", label: "Locked" },
    ],
    Favourited: [
      { value: "is_locked", label: "Locked" },
      { value: "is_live", label: "Sync Clones" },
    ],
    Unlocked: [
      { value: "lock_password", label: "Password" },
      { value: "lock_access", label: "Granted Access" },
      { value: "lock_payment", label: "Purchased" },
      { value: "is_live", label: "Sync Clones" },
    ],
    Created: [
      { value: "is_public", label: "Public" },
      ...(isLoggedInUserProfile
        ? [{ value: "is_private", label: "Private" }]
        : []),
      { value: "is_edited_clone", label: "Edited Clones" },
      ...(isLoggedInUserProfile
        ? [{ value: "gallery_visibility", label: "Folders - Hidden Content" }]
        : []),
    ],
  };

  return (
    <>
      <div
        style={{
          position: "sticky",
          top: "0px",
          backgroundColor: colors.white,
          zIndex: 999,
        }}
      >
        <ListHeader
          searchedText={searchedText}
          onSearchChange={(text) => setSearchedText(text)}
          onSearchSubmit={() => console.log("Search submitted:", searchedText)}
          isGridSelect={isGridSelect}
          onViewToggle={setIsGridSelect}
          isUserProfile={isLoggedInUserProfile}
          tabChild={tabChild}
        />
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{ borderBottom: "1px solid #EAECF0", mb: 2 }}
        >
          <CustomTabs
            variant="standard"
            sx={{}}
            categoriesWithIcons={[
              {
                label: "Created",
                value: WheelCategories.Created,
                icon: <MagicWand />,
                suffix:
                  !isTabCountLoading && tabCounts?.created
                    ? `${tabCounts.created}`
                    : "0",
              },
              {
                label: "Cloned",
                value: WheelCategories.Cloned,
                icon: <CloneIcon />,
                suffix:
                  !isTabCountLoading && tabCounts?.cloned
                    ? `${tabCounts.cloned}`
                    : "0",
              },
              ...(isLoggedInUserProfile
                ? [
                    {
                      label: "Unlocked",
                      value: WheelCategories.Unlocked,
                      icon: <UnlcockedIcon />,
                      suffix:
                        !isTabCountLoading && tabCounts?.unlocked
                          ? `${tabCounts.unlocked}`
                          : "0",
                    },
                    {
                      label: "Favorited",
                      value: WheelCategories.Favourited,
                      icon: <HeartIcon />,
                      suffix:
                        !isTabCountLoading && tabCounts?.favorited
                          ? `${tabCounts.favorited}`
                          : "0",
                    },
                  ]
                : []),
            ]}
            selectedCategory={selectedWheelCategory}
            onCategoryChange={handleTabChange}
          />

          <MultiSelect
            options={filterOptionsByCategory[selectedWheelCategory] || []}
            selectedValues={filtersByCategory[selectedWheelCategory] || []}
            onChange={handleFilterChange}
            menuStyle={{ zIndex: 999999 }}
          >
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              gap={1}
              direction={"row"}
              style={{ cursor: "pointer" }}
            >
              <div>
                <Filter width={16} height={16} />
              </div>
              <TextView variant="largeText">Filter</TextView>
            </Stack>
          </MultiSelect>
        </Stack>
      </div>
      <List
        wheels={allWheels}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        isError={isError}
        error={error}
        onWheelDelete={onWheelDelete}
        onWheelReactivate={onWheelReactivate}
        wheelCategory={selectedWheelCategory}
        isGridView={isGridSelect}
      />
    </>
  );
};

export default WheelsTab;
